// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import siteMessage from "@insite/client-framework/SiteMessage";
import {
    getErrorMessage,
    getProductsForProductInfoList,
} from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DataTable from "@insite/mobius/DataTable";
import { DataTableBody, DataTableHead, DataTableHeader } from "@insite/mobius/index";
import HotSheetTableProducts from "@stanion/App/Components/HotSheetTableProducts/HotSheetTableProducts";
import Notify from "@stanion/App/Components/Notify";
import TitleTypography, { TitleTypographyStyles } from "@stanion/App/Components/TitleTypography/TitleTypography";
import LoadSheetProducts from "@stanion/App/Handlers/HotSheet/HotSheetHandler";
import { CustomAppState } from "@stanion/App/Store/Reducers";
import { xIsAuthenticated } from "@stanion/App/Store/Selectors/GeneralSelectors";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum productFields {
    productNumber = "productNumber",
    productNameOverride = "productNameOverride",
}

const enum fields {
    title = "title",
    productNumbers = "productNumbers",
}

interface ProductFields {
    fields: {
        [productFields.productNumber]: string;
        [productFields.productNameOverride]: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.productNumbers]: ProductFields[];
    };
}

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => ({
    products: getProductsForProductInfoList(state, ownProps.id),
    errorMessage: getErrorMessage(state, ownProps.id),
    isAuthenticated: xIsAuthenticated(state),
});

const mapDispatchToProps = {
    LoadSheetProducts,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasShellContext;

export interface ConduitHotSheetTabelStyles {
    titleTypography?: TitleTypographyStyles;
}

export const conduitHotSheetTabelStyles: ConduitHotSheetTabelStyles = {};

const styles = conduitHotSheetTabelStyles;

const ConduitHotSheetTabel = ({
    products,
    fields,
    id,
    LoadSheetProducts,
    shellContext,
    isAuthenticated,
    errorMessage,
}: Props) => {
    useEffect(() => {
        if (isAuthenticated) {
            LoadSheetProducts({
                widgetId: id,
                productNumbers: fields.productNumbers?.map((line: ProductFields) => line.fields.productNumber),
                isShellEdit: shellContext.isEditing,
            });
        }
    }, []);

    if (errorMessage && shellContext.isInShell) {
        return (
            <Notify type="danger">
                {translate(`Failed to load products. Error: '${errorMessage}'. Please check widget settings.`)}
            </Notify>
        );
    }
    if (!products || products.length === 0 || !isAuthenticated) {
        return null;
    }

    const titleMap = fields.productNumbers?.reduce(
        (acc: any, item: ProductFields) => ({ ...acc, [item.fields.productNumber]: item.fields.productNameOverride }),
        {},
    );

    return (
        <StyledWrapper data-test-selector="hot-sheet-table">
            <TitleTypography extendedStyles={styles.titleTypography}>{translate(fields.title)}</TitleTypography>
            <DataTable>
                <DataTableHead>
                    <DataTableHeader>{siteMessage("Custom_HotSheet_Size")}</DataTableHeader>
                    <DataTableHeader>{siteMessage("Custom_HotSheet_Price")}</DataTableHeader>
                </DataTableHead>
                <DataTableBody>
                    {products.map(product => (
                        <HotSheetTableProducts
                            key={product.id}
                            product={product}
                            id={id}
                            title={fields.title}
                            productTitle={titleMap?.[product?.productNumber]}
                        />
                    ))}
                </DataTableBody>
            </DataTable>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(ConduitHotSheetTabel)),
    definition: {
        group: "Hot Sheet" as any,
        displayName: "Hot Sheet",
        allowedContexts: ["HotSheetPage"] as any,
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
                sortOrder: 1,
            },
            {
                name: fields.productNumbers,
                displayName: "Product Numbers",
                editorTemplate: "ListField",
                isRequired: true,
                getDisplay: (item: HasFields) => {
                    return item.fields.productNameOverride || item.fields.productNumber;
                },
                fieldDefinitions: [
                    {
                        name: productFields.productNumber,
                        displayName: "Product Number",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: productFields.productNameOverride,
                        displayName: "Product Name Override",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: false,
                    },
                ],
                fieldType: "General",
                defaultValue: [],
            },
        ],
    },
};

export default widgetModule;
